import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { flashToast } from '../util/toast.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import type {
  ExpeditionCatalogEntry,
  ActiveExpeditionDto,
  ExpeditionRewardDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

export async function renderExpeditions(root: HTMLElement): Promise<void> {
  let catalog: ExpeditionCatalogEntry[] = [];
  let active: ActiveExpeditionDto | null = null;
  let flash: FlashState | null = null;
  let lastRewards: { rewards: ExpeditionRewardDto[]; crownsGained: string; xpGained: number; levelsGained: number } | null = null;

  async function refresh(): Promise<void> {
    try {
      const me = await api.me();
      store.set({ account: me.account, character: me.character });
      const r = await api.listExpeditions();
      catalog = r.catalog;
      active = r.active;
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load.';
      flash = { kind: 'danger', message: msg };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/expeditions.webp" alt="" />
        <h1>Expeditions</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Long journeys. Rare reward. Some never return.
        </p>

        <div id="flash"></div>
        ${lastRewards ? renderRewardsBanner(lastRewards) : ''}
        <div id="active-panel"></div>
        <h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">The Catalog</h2>
        <div id="catalog"></div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .exp-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex; flex-direction: column;
        }
        /* Table hugs the blurb text; the embark button pins to the card bottom. */
        .exp-card .cg { margin-bottom: auto; }
        .exp-card.locked { opacity: 0.55; }
        .exp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--s-3);
        }
        .pill {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 2px 10px;
          font-family: var(--font-numeric);
          font-size: 0.85rem;
        }
        /* (gold pill styling lives in pages.css — don't override here) */
        .pill.success { border-color: var(--c-success); color: var(--c-success); }
        .progress-bar {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          height: 18px;
          overflow: hidden;
          margin: var(--s-2) 0;
        }
        .progress-fill {
          background: linear-gradient(90deg, var(--c-gold) 0%, var(--c-gold-bright) 100%);
          height: 100%;
          transition: width 0.5s;
        }
        .reward-tier {
          font-family: var(--font-display);
          font-size: 0.8rem;
          padding: 1px 6px;
          border: 1px solid var(--c-rule);
          border-radius: 3px;
          background: var(--c-parchment-dark);
          margin-left: 6px;
        }
        .reward-tier.mythic    { color: var(--c-gold); border-color: var(--c-gold); background: #2a1a08; }
        .reward-tier.artifact  { color: var(--c-oxblood); border-color: var(--c-oxblood); }
        .reward-tier.legendary { color: var(--c-gold-bright); border-color: var(--c-gold); }
      `;
      main.appendChild(style);

      // Flash → floating toast (overlays in place; no scroll-to-top).
      if (flash) flashToast(flash);

      // Active panel
      const activeRoot = main.querySelector<HTMLDivElement>('#active-panel')!;
      if (active) {
        renderActive(activeRoot, active, refresh, (f) => (flash = f), (r) => (lastRewards = r), renderMain);
      }

      // Catalog
      const catalogRoot = main.querySelector<HTMLDivElement>('#catalog')!;
      renderCatalog(catalogRoot, catalog, char.level, active !== null, refresh, (f) => (flash = f), renderMain);
    };

    anchored(renderMain);
  });
}

function renderRewardsBanner(r: { rewards: ExpeditionRewardDto[]; crownsGained: string; xpGained: number; levelsGained: number }): string {
  return `
    <div class="alert success" style="margin-bottom: var(--s-3);">
      <strong>Expedition complete!</strong> +${formatCrowns(r.crownsGained)} crowns, +${formatBigNum(r.xpGained)} XP
      ${r.levelsGained > 0 ? `, <strong>${r.levelsGained} level${r.levelsGained === 1 ? '' : 's'} gained</strong>` : ''}.
      <br/>
      Rewards: ${r.rewards.map((rd) => `${escapeHtml(rd.itemName)} ×${rd.qty}<span class="reward-tier ${rd.tier}">${rd.tier.toUpperCase()}</span>`).join(', ')}
    </div>
  `;
}

function renderActive(
  content: HTMLDivElement,
  a: ActiveExpeditionDto,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  setRewards: (r: { rewards: ExpeditionRewardDto[]; crownsGained: string; xpGained: number; levelsGained: number }) => void,
  rerender: () => void,
): void {
  const totalSeconds = a.durationSeconds;
  const elapsed = totalSeconds - a.remainingSeconds;
  const pct = Math.min(100, Math.max(0, (elapsed / totalSeconds) * 100));
  const remStr = formatRemaining(a.remainingSeconds);

  content.innerHTML = `
    <section class="panel">
      <h2 style="margin-top:0;">On Expedition: ${escapeHtml(a.name)}</h2>
      <p style="font-style:italic; color: var(--c-ink-soft);">"${escapeHtml(a.blurb)}"</p>
      <div class="progress-bar"><div class="progress-fill" style="width: ${pct.toFixed(1)}%;"></div></div>
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin-top: var(--s-2);">
        <span class="pill">${formatDuration(a.durationSeconds)}</span>
        <span class="pill ${a.ready ? 'success' : ''}">${a.ready ? 'Ready to claim' : remStr + ' remaining'}</span>
        <span class="pill">Departed ${new Date(a.startedAt).toLocaleDateString()}</span>
        <span class="pill">Returns ${new Date(a.endsAt).toLocaleDateString()}</span>
      </div>
      <div style="display:flex; gap: var(--s-2); margin-top: var(--s-3); flex-wrap:wrap;">
        <button class="btn" id="claim-btn" ${a.ready ? '' : 'disabled'}>
          ${a.ready ? 'Claim Rewards' : 'Not yet ready'}
        </button>
        <button class="btn ghost" id="abandon-btn">Abandon (forfeit all rewards)</button>
      </div>
    </section>
  `;

  content.querySelector<HTMLButtonElement>('#claim-btn')?.addEventListener('click', async () => {
    try {
      const r = await api.claimExpedition();
      store.set({ character: r.character });
      setRewards({
        rewards: r.rewards,
        crownsGained: r.crownsGained,
        xpGained: r.xpGained,
        levelsGained: r.levelsGained,
      });
      setFlash({ kind: 'success', message: 'Expedition claimed.' });
      await refresh();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Claim failed.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      rerender();
    }
  });

  content.querySelector<HTMLButtonElement>('#abandon-btn')?.addEventListener('click', async () => {
    if (!(await showConfirmDialog({
      title: 'Abandon the expedition?',
      message: 'All progress and rewards will be forfeited. This cannot be undone.',
      confirmLabel: 'Abandon',
    }))) return;
    try {
      const r = await api.abandonExpedition();
      store.set({ character: r.character });
      setFlash({ kind: 'warn', message: 'Expedition abandoned.' });
      await refresh();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Abandon failed.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      rerender();
    }
  });
}

function renderCatalog(
  content: HTMLDivElement,
  list: ExpeditionCatalogEntry[],
  charLevel: number,
  hasActive: boolean,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  content.innerHTML = `
    <div class="exp-grid">
      ${list
        .map((e) => {
          const locked = !e.unlocked;
          const blockedByActive = hasActive;
          const btnDisabled = locked || blockedByActive;
          const btnLabel = locked
            ? `Locked (level ${e.levelGate})`
            : blockedByActive
              ? 'Another expedition active'
              : `Embark (${formatDuration(e.durationSeconds)})`;
          return `
        <div class="exp-card ${locked ? 'locked' : ''}">
          <h3 style="margin: 0 0 4px 0;">${escapeHtml(e.name)}</h3>
          <p style="font-style:italic; color: var(--c-ink-soft); font-size: 0.9rem; margin: 0 0 var(--s-2) 0;">"${escapeHtml(e.blurb)}"</p>
          ${cgTable([
            cgCol('Requires', [
              cgRow('Duration', formatDuration(e.durationSeconds)),
              cgRow('Level', `${e.levelGate}+`, locked ? 'warn' : ''),
            ]),
            cgCol('Reward · guaranteed', [
              cgRow('Crowns', `+${formatCrowns(e.guaranteedCrowns)}`, 'gain'),
              cgRow('XP', `+${formatBigNum(e.guaranteedXp)}`, 'gain'),
            ]),
          ])}
          <button class="btn" data-slug="${e.slug}" ${btnDisabled ? 'disabled' : ''}>
            ${btnLabel}
          </button>
        </div>
      `;
        })
        .join('')}
    </div>
  `;

  content.querySelectorAll<HTMLButtonElement>('button[data-slug]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const slug = btn.dataset.slug!;
      const ok = await showConfirmDialog({
        title: 'Begin expedition?',
        message: 'Begin this expedition? You can claim rewards when it completes.',
        confirmLabel: 'Set out',
        tone: 'gold',
      });
      if (!ok) return;
      btn.disabled = true;
      try {
        await api.startExpedition({ expeditionSlug: slug });
        setFlash({ kind: 'success', message: 'Expedition begun.' });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Could not embark.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });
}

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return 'now';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 30) {
    const months = Math.floor(days / 30);
    const remDays = days % 30;
    return `${months}mo ${remDays}d`;
  }
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Total-duration label in the largest whole unit (e.g. "2 hours", "3 days",
 *  "2 weeks", "6 months"). */
function formatDuration(seconds: number): string {
  const HOUR = 3600, DAY = 86400, WEEK = 604800, MONTH = 2592000;
  const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`;
  if (seconds >= MONTH && seconds % MONTH === 0) return plural(seconds / MONTH, 'month');
  if (seconds >= WEEK && seconds % WEEK === 0) return plural(seconds / WEEK, 'week');
  if (seconds >= DAY && seconds % DAY === 0) return plural(seconds / DAY, 'day');
  return plural(Math.round(seconds / HOUR), 'hour');
}

